import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faArrowRightFromBracket, faGear, faUser } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { CSSProperties } from 'react';
import { IconButton } from '@/design-system/primitives/IconButton';
import { MenuPopover, useMenuPopoverClose } from '@/design-system/primitives/MenuPopover';
import { type ClassName, twx } from '@/utils/tailwind';

type FAIconStyle = CSSProperties & { [key: `--fa-${string}`]: string };

const itemIconStyle: FAIconStyle = {
  width: 16,
  height: 16,
  '--fa-primary-color': 'var(--color-secondary-focal)',
  '--fa-secondary-color': 'var(--color-secondary-main)',
  '--fa-secondary-opacity': '1',
};

interface UserMenuItemProps {
  icon: IconDefinition;
  label: string;
  onSelect: () => void;
}

/** One row in the account menu. Dismisses the popover before firing its
 *  action so a navigation/reload doesn't leave the menu visibly open. */
const UserMenuItem = ({ icon, label, onSelect }: UserMenuItemProps) => {
  const close = useMenuPopoverClose();
  return (
    <button
      type="button"
      onClick={() => {
        close();
        onSelect();
      }}
      className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-content-text transition hover:bg-secondary-subtle"
    >
      <FontAwesomeIcon icon={icon} style={itemIconStyle} />
      {label}
    </button>
  );
};

export interface UserMenuProps {
  /** Admins get an extra "Admin" entry linking to the admin panel. */
  isAdmin: boolean;
  onOpenAdmin: () => void;
  onSignOut: () => void;
  className?: ClassName;
}

/** Account menu behind a user-icon trigger in the header chip. Discloses the
 *  admin panel link (admins only) and sign-out, keeping them out of the
 *  always-visible action row. */
export const UserMenu = ({ isAdmin, onOpenAdmin, onSignOut, className }: UserMenuProps) => (
  <MenuPopover
    className={twx('shrink-0', className)}
    align="right"
    width="xs"
    renderTrigger={({ ref, onClick }) => (
      <IconButton
        ref={ref}
        icon={faUser}
        ariaLabel="Account menu"
        tone="inverse"
        shape="circle"
        size="base"
        iconSize={18}
        duotoneVariant="inverse"
        onClick={onClick}
      />
    )}
  >
    <div className="space-y-0.5 p-1.5">
      {isAdmin && <UserMenuItem icon={faGear} label="Admin" onSelect={onOpenAdmin} />}
      <UserMenuItem icon={faArrowRightFromBracket} label="Sign out" onSelect={onSignOut} />
    </div>
  </MenuPopover>
);
